import { IResponsiveProps } from '@/hooks/useResponsiveProps';

/**
 * Allowed rounded corner sizes for the AtMedia image.
 *
 * - `'none'` – No border radius
 * - `'sm'` through `'3xl'` – Tailwind-style size tokens
 */
export type TAtMediaRoundedSizes =
  | 'none'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl';

/**
 * Visual + semantic intent of the image.
 *
 * - `DEFAULT` – Standard image with accessible alt and role
 * - `BACKGROUND` – Decorative image used as a visual background
 */
export enum EImageIntents {
  DEFAULT = 'default',
  BACKGROUND = 'background',
}

/**
 * Controls the focal point of the image when cropped or styled via object-position.
 *
 * - `LEFT` – Image aligns to the left
 * - `CENTER` – Image centered (default)
 * - `RIGHT` – Image aligns to the right
 */
export enum EImagePosition {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

/**
 * Predefined aspect ratios supported by AtMedia.
 *
 * - `'16/9'`, `'4/3'`, etc. – Used for responsive containers and crop logic
 * - `'auto'` – No aspect ratio enforced
 */
export type TMediaAspectRatio =
  | '16/9'
  | '3/2'
  | '1/1'
  | '4/3'
  | '2/3'
  | '21/9'
  | '9/16'
  | 'auto';

/**
 * Props for the `AtMedia` component.
 * Allows full control over responsive image layout, accessibility, and styling.
 */
export interface IAtMediaProps {
  /** Optional Tailwind classes for the image element */
  className?: string;
  /** Size of the border radius, using Tailwind token names */
  roundedCorners?: TAtMediaRoundedSizes;
  /** Image source — supports responsive object format */
  src: IResponsiveProps<string>;
  /** Alt text for screen readers — can be responsive or a string */
  alt: IResponsiveProps<string> | string;
  /** Intent determines accessibility role and styling behavior */
  intent?: EImageIntents;
  /** Focal point of the image per breakpoint */
  position?: IResponsiveProps<EImagePosition | string>;
  /** Whether this image should be loaded with high priority (Next.js) */
  priority?: boolean;
  /** Image quality passed to Next.js (1–100) */
  quality?: number;
  /** If true, hides image from screen readers and sets role="none" */
  decorative?: boolean;
  /** Sizes prop passed to Next.js for responsive optimization */
  sizes?: string;
  /** Aspect ratio for responsive layout per breakpoint */
  aspectRatio?: IResponsiveProps<TMediaAspectRatio | string>;
  /** If true, shows a visual overlay on top of the image */
  showOverlay?: boolean;
  /** Additional classes for the figure element */
  figureClasses?: string;
}
